import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  FileText, Download, RefreshCw, Globe, Calendar, 
  DollarSign, Receipt, PieChart, TrendingUp, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRIES = [
  { code: 'AU', name: 'Australia', flag: '🇦🇺', taxOffice: 'ATO', taxType: 'GST', symbol: 'A$' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧', taxOffice: 'HMRC', taxType: 'VAT', symbol: '£' },
  { code: 'US', name: 'United States', flag: '🇺🇸', taxOffice: 'IRS', taxType: 'Income Tax', symbol: '$' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', taxOffice: 'IRD', taxType: 'GST', symbol: 'NZ$' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', taxOffice: 'CRA', taxType: 'GST/HST', symbol: 'C$' },
  { code: 'IN', name: 'India', flag: '🇮🇳', taxOffice: 'GST Council', taxType: 'GST (CGST+SGST/IGST)', symbol: '₹' }
];

const REPORTS = [
  { id: 'gst-summary', name: 'GST/VAT Summary', icon: Receipt, description: 'Total tax paid for BAS/VAT returns' },
  { id: 'bas-worksheet', name: 'BAS Worksheet', icon: FileText, description: 'Pre-filled G10, G11, G13, G14 fields', countryOnly: 'AU' },
  { id: 'gstr-3b', name: 'GSTR-3B Summary', icon: FileText, description: 'Monthly GST return with ITC details', countryOnly: 'IN' },
  { id: 'gstr-2a', name: 'GSTR-2A Register', icon: Receipt, description: 'Purchase register for ITC reconciliation', countryOnly: 'IN' },
  { id: 'expenses-by-category', name: 'Expenses by Category', icon: PieChart, description: 'Tax-compliant expense categories' },
  { id: 'financial-year-summary', name: 'Financial Year Summary', icon: Calendar, description: 'Complete FY overview for tax returns' },
  { id: 'tax-deductions', name: 'Tax Deductions', icon: TrendingUp, description: 'Claimable business expenses' }
];

function TaxReports() {
  const [country, setCountry] = useState('AU');
  const [financialYear, setFinancialYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [activeReport, setActiveReport] = useState('gst-summary');

  const currentCountry = COUNTRIES.find(c => c.code === country);

  const fetchReport = async (reportId) => {
    setLoading(true);
    setActiveReport(reportId);
    try {
      let endpoint;
      
      if (reportId === 'bas-worksheet') {
        endpoint = `${API}/tax/bas-worksheet?financial_year=${financialYear}`;
      } else if (reportId === 'gstr-3b' || reportId === 'gstr-2a') {
        // Indian GST reports - use current month
        const currentMonth = new Date().getMonth() + 1;
        endpoint = `${API}/tax/${reportId}?financial_year=${financialYear}&month=${currentMonth}`;
      } else {
        endpoint = `${API}/tax/${reportId}?country=${country}&financial_year=${financialYear}`;
      }
      
      const response = await axios.get(endpoint, { withCredentials: true });
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (reportId) => {
    try {
      const response = await axios.get(
        `${API}/tax/export/${reportId}?country=${country}&financial_year=${financialYear}&format=csv`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportId}_${country}_FY${financialYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report downloaded!');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  useEffect(() => {
    fetchReport(activeReport);
  }, [country, financialYear]);

  const renderReportContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
        </div>
      );
    }

    if (!reportData) return null;
    
    const currencySymbol = currentCountry?.symbol || '$';

    switch (activeReport) {
      case 'gst-summary':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-sm text-slate-500 mb-1">Total Purchases (inc. tax)</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {currencySymbol}{reportData.summary?.total_purchases_inc_tax?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-sm text-slate-500 mb-1">Total Purchases (ex. tax)</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {currencySymbol}{reportData.summary?.total_purchases_ex_tax?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-4">
                <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">Total {reportData.tax_type} Paid</p>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                  {currencySymbol}{reportData.summary?.total_tax_paid?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-sm text-slate-500 mb-1">Invoices Processed</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {reportData.summary?.invoice_count || 0}
                </p>
              </div>
            </div>
            
            {/* India GST Breakdown */}
            {reportData.india_gst_breakdown && (
              <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
                <h4 className="font-semibold text-indigo-700 dark:text-indigo-300 mb-3">GST Breakdown (Input Tax Credit)</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400">CGST</p>
                    <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
                      ₹{reportData.india_gst_breakdown.cgst?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400">SGST</p>
                    <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
                      ₹{reportData.india_gst_breakdown.sgst?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400">IGST</p>
                    <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300">
                      ₹{reportData.india_gst_breakdown.igst?.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">Total ITC</p>
                    <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                      ₹{reportData.india_gst_breakdown.total_itc?.toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-2">{reportData.india_gst_breakdown.note}</p>
              </div>
            )}
            
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                This is input tax credit (purchases only). Add your sales/GST collected to complete your {country === 'IN' ? 'GSTR-3B' : 'BAS'}.
              </p>
            </div>
          </div>
        );

      case 'bas-worksheet':
        return (
          <div className="space-y-4">
            {reportData.bas_fields && Object.entries(reportData.bas_fields).map(([field, data]) => (
              <div key={field} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-indigo-600">{field}</span>
                    <span className="text-slate-700 dark:text-slate-300">{data.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{data.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    A${data.amount?.toLocaleString() || '0'}
                  </p>
                  {data.gst_claimable && (
                    <p className="text-sm text-emerald-600">GST: A${data.gst_claimable}</p>
                  )}
                </div>
              </div>
            ))}
            <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-4 flex items-center justify-between">
              <span className="font-semibold text-emerald-700 dark:text-emerald-300">Total GST Claimable</span>
              <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                A${reportData.totals?.total_gst_claimable?.toLocaleString() || '0'}
              </span>
            </div>
          </div>
        );

      case 'gstr-3b':
        return (
          <div className="space-y-6">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
              <h4 className="font-semibold text-indigo-700 dark:text-indigo-300 mb-1">Return Period</h4>
              <p className="text-indigo-600 dark:text-indigo-400">{reportData.return_period}</p>
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-1">Taxable Value</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">
                  ₹{reportData.summary?.total_taxable_value?.toLocaleString()}
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-4">
                <p className="text-xs text-blue-600 mb-1">IGST</p>
                <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                  ₹{reportData.summary?.total_igst?.toLocaleString()}
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-4">
                <p className="text-xs text-purple-600 mb-1">CGST</p>
                <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
                  ₹{reportData.summary?.total_cgst?.toLocaleString()}
                </p>
              </div>
              <div className="bg-pink-50 dark:bg-pink-900/30 rounded-xl p-4">
                <p className="text-xs text-pink-600 mb-1">SGST</p>
                <p className="text-lg font-bold text-pink-700 dark:text-pink-300">
                  ₹{reportData.summary?.total_sgst?.toLocaleString()}
                </p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-4">
                <p className="text-xs text-emerald-600 mb-1">Total ITC</p>
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                  ₹{reportData.summary?.total_itc?.toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Table 4 - Eligible ITC */}
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Table 4 - Eligible ITC</h4>
              <div className="space-y-3">
                {reportData.table_4 && Object.entries(reportData.table_4).filter(([key]) => key !== 'description').map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700 last:border-0">
                    <div>
                      <span className="font-mono text-xs text-indigo-600">{key}</span>
                      <p className="text-sm text-slate-700 dark:text-slate-300">{data.label}</p>
                    </div>
                    <div className="flex gap-4 text-right text-sm">
                      <div><span className="text-slate-400">IGST:</span> <span className="font-medium">₹{data.igst}</span></div>
                      <div><span className="text-slate-400">CGST:</span> <span className="font-medium">₹{data.cgst}</span></div>
                      <div><span className="text-slate-400">SGST:</span> <span className="font-medium">₹{data.sgst}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Rate-wise Summary */}
            {reportData.rate_wise_summary && Object.keys(reportData.rate_wise_summary).length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Rate-wise Summary</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left py-2 text-slate-500">GST Rate</th>
                        <th className="text-right py-2 text-slate-500">Taxable Value</th>
                        <th className="text-right py-2 text-slate-500">IGST</th>
                        <th className="text-right py-2 text-slate-500">CGST</th>
                        <th className="text-right py-2 text-slate-500">SGST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(reportData.rate_wise_summary).map(([rate, data]) => (
                        <tr key={rate} className="border-b border-slate-100 dark:border-slate-700">
                          <td className="py-2 font-medium">{rate}%</td>
                          <td className="text-right">₹{data.taxable_value?.toLocaleString()}</td>
                          <td className="text-right">₹{data.igst?.toLocaleString()}</td>
                          <td className="text-right">₹{data.cgst?.toLocaleString()}</td>
                          <td className="text-right">₹{data.sgst?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                {reportData.note}
              </p>
            </div>
          </div>
        );

      case 'gstr-2a':
        return (
          <div className="space-y-6">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
              <h4 className="font-semibold text-indigo-700 dark:text-indigo-300 mb-1">Return Period</h4>
              <p className="text-indigo-600 dark:text-indigo-400">{reportData.return_period}</p>
            </div>
            
            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-1">Total Vendors</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {reportData.totals?.total_vendors}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-1">Total Invoices</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {reportData.totals?.total_invoices}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-1">Taxable Value</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  ₹{reportData.totals?.total_taxable?.toLocaleString()}
                </p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-4">
                <p className="text-xs text-emerald-600 mb-1">Total ITC</p>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                  ₹{reportData.totals?.total_itc?.toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Vendor-wise breakdown */}
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Vendor-wise Purchase Register</h4>
              <div className="space-y-4">
                {reportData.vendors?.slice(0, 10).map((vendor, i) => (
                  <div key={i} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{vendor.vendor_name}</p>
                        {vendor.gstin && <p className="text-xs text-slate-500">GSTIN: {vendor.gstin}</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900 dark:text-white">₹{vendor.total_taxable?.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">{vendor.invoice_count} invoices</p>
                      </div>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span className="text-blue-600">IGST: ₹{vendor.total_igst?.toLocaleString()}</span>
                      <span className="text-purple-600">CGST: ₹{vendor.total_cgst?.toLocaleString()}</span>
                      <span className="text-pink-600">SGST: ₹{vendor.total_sgst?.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                {reportData.note}
              </p>
            </div>
          </div>
        );

      case 'expenses-by-category':
        return (
          <div className="space-y-4">
            {reportData.categories?.map((cat, i) => (
              <div key={i} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{cat.category_name}</p>
                  <p className="text-xs text-slate-500">{cat.invoice_count} invoices</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900 dark:text-white">{currencySymbol}{cat.total_amount?.toLocaleString()}</p>
                  <p className="text-sm text-emerald-600">{country === 'IN' ? 'GST' : reportData.tax_type || 'GST'}: {currencySymbol}{cat.total_gst}</p>
                </div>
              </div>
            ))}
            <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-xl p-4 flex items-center justify-between">
              <span className="font-semibold text-indigo-700 dark:text-indigo-300">Total</span>
              <div className="text-right">
                <span className="text-xl font-bold text-indigo-700 dark:text-indigo-300">
                  {currencySymbol}{reportData.totals?.total_amount?.toLocaleString() || '0'}
                </span>
                <p className="text-sm text-indigo-600">GST: {currencySymbol}{reportData.totals?.total_gst}</p>
              </div>
            </div>
          </div>
        );

      case 'financial-year-summary':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-sm text-slate-500 mb-1">Total Expenses</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {currencySymbol}{reportData.summary?.total_expenses?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-4">
                <p className="text-sm text-emerald-600 mb-1">Total {country === 'IN' ? 'GST' : reportData.tax_type || 'GST'} Paid</p>
                <p className="text-2xl font-bold text-emerald-700">
                  {currencySymbol}{reportData.summary?.total_gst_paid?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-sm text-slate-500 mb-1">Unique Vendors</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {reportData.summary?.unique_vendors || 0}
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Top Vendors</h4>
              <div className="space-y-2">
                {reportData.top_vendors?.slice(0, 5).map((v, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700">
                    <span className="text-slate-700 dark:text-slate-300">{v.vendor}</span>
                    <span className="font-medium text-slate-900 dark:text-white">{currencySymbol}{v.amount?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'tax-deductions':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-4">
                <p className="text-sm text-emerald-600 mb-1">Fully Deductible</p>
                <p className="text-2xl font-bold text-emerald-700">
                  {currencySymbol}{reportData.totals?.fully_deductible?.toLocaleString() || '0'}
                </p>
                <p className="text-xs text-emerald-600">{reportData.counts?.fully_deductible} items</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/30 rounded-xl p-4">
                <p className="text-sm text-amber-600 mb-1">Partially Deductible</p>
                <p className="text-2xl font-bold text-amber-700">
                  {currencySymbol}{reportData.totals?.partially_deductible?.toLocaleString() || '0'}
                </p>
                <p className="text-xs text-amber-600">{reportData.counts?.partially_deductible} items</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <p className="text-sm text-slate-500 mb-1">Non-Deductible</p>
                <p className="text-2xl font-bold text-slate-400">
                  {currencySymbol}{reportData.totals?.non_deductible?.toLocaleString() || '0'}
                </p>
                <p className="text-xs text-slate-400">{reportData.counts?.non_deductible} items</p>
              </div>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-xl p-4 flex items-center justify-between">
              <span className="font-semibold text-indigo-700 dark:text-indigo-300">Total Claimable</span>
              <span className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">
                {currencySymbol}{reportData.totals?.total_claimable?.toLocaleString() || '0'}
              </span>
            </div>
            <p className="text-xs text-slate-500">{reportData.note}</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Country Selector */}
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-400" />
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
            >
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>{c.flag} {c.name} ({c.taxOffice})</option>
              ))}
            </select>
          </div>

          {/* Year Selector */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <select
              value={financialYear}
              onChange={(e) => setFinancialYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
            >
              {[2026, 2025, 2024, 2023].map(year => (
                <option key={year} value={year}>FY{year}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-sm text-slate-500">
          {currentCountry?.flag} {currentCountry?.taxOffice} • {currentCountry?.taxType}
        </div>
      </div>

      {/* Report Selector */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {REPORTS.filter(r => !r.countryOnly || r.countryOnly === country).map(report => (
          <button
            key={report.id}
            onClick={() => fetchReport(report.id)}
            data-testid={`report-btn-${report.id}`}
            className={`p-3 rounded-xl text-left transition-all ${
              activeReport === report.id
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            <report.icon className={`w-5 h-5 mb-2 ${activeReport === report.id ? 'text-white' : 'text-indigo-600'}`} />
            <p className="font-medium text-sm">{report.name}</p>
          </button>
        ))}
      </div>

      {/* Report Content */}
      <motion.div
        key={activeReport}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {REPORTS.find(r => r.id === activeReport)?.name}
            </h3>
            <p className="text-sm text-slate-500">{reportData?.period}</p>
          </div>
          <button
            onClick={() => handleDownload(activeReport)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            Download CSV
          </button>
        </div>

        {renderReportContent()}
      </motion.div>
    </div>
  );
}

export default TaxReports;
